import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  ArrowLeft,
  Download,
  Send,
  Mail,
  Printer,
  Edit,
  MoreVertical,
  DollarSign,
  Calendar,
  Building2,
  FileText,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  useInvoice,
  useSendInvoice,
  useCancelInvoice,
  useAddPayment,
  useDeletePayment,
} from '@/hooks/useInvoices';
import { useSendInvoiceEmail } from '@/hooks/useInvoiceEmail';
import { downloadInvoicePDF } from '@/services/invoice/invoicePdfService';
import { INVOICE_STATUS_COLORS, INVOICE_STATUS_LABELS } from '@/types/invoice.types';
import type { InvoicePaymentFormData } from '@/types/invoice.types';

export default function InvoiceDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState<InvoicePaymentFormData>({
    amount: 0,
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    payment_method: '',
    reference_number: '',
    notes: '',
  });
  const [sendEmail, setSendEmail] = useState('');
  const [sendMessage, setSendMessage] = useState('');

  const { data: invoice, isLoading } = useInvoice(id!);
  const sendInvoice = useSendInvoice();
  const cancelInvoice = useCancelInvoice();
  const addPayment = useAddPayment();
  const deletePayment = useDeletePayment();
  const sendInvoiceEmail = useSendInvoiceEmail();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-semibold">Invoice not found</h2>
        <Link to="/invoices">
          <Button className="mt-4">Back to Invoices</Button>
        </Link>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: invoice.currency,
    }).format(amount);
  };

  const totalPaid = invoice.payments?.reduce((sum, p) => sum + p.amount, 0) || 0;
  const remainingBalance = invoice.total_amount - totalPaid;

  const handleSendInvoice = async () => {
    if (!sendEmail.trim()) {
      toast({
        title: 'Email Required',
        description: 'Please enter an email address to send the invoice to.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Send the email with PDF attachment
      await sendInvoiceEmail.mutateAsync({
        invoice,
        options: {
          to: sendEmail,
          message: sendMessage,
        },
      });
      
      // Update invoice status
      await sendInvoice.mutateAsync(invoice.id);
      
      setShowSendDialog(false);
      setSendEmail('');
      setSendMessage('');
    } catch (error) {
      // Error is handled by the mutation hook
    }
  };

  const handleCancelInvoice = async () => {
    if (confirm('Are you sure you want to cancel this invoice?')) {
      try {
        await cancelInvoice.mutateAsync(invoice.id);
        toast({
          title: 'Invoice Cancelled',
          description: `Invoice ${invoice.invoice_number} has been cancelled.`,
        });
      } catch (error) {
        // Error handling omitted intentionally
      }
    }
  };

  const handleAddPayment = async () => {
    try {
      await addPayment.mutateAsync({
        invoiceId: invoice.id,
        payment: paymentForm,
      });
      setShowPaymentDialog(false);
      setPaymentForm({
        amount: 0,
        payment_date: format(new Date(), 'yyyy-MM-dd'),
        payment_method: '',
        reference_number: '',
        notes: '',
      });
    } catch (error) {
      // Error handling omitted intentionally
    }
  };

  const handleDeletePayment = async (paymentId: string) => {
    if (confirm('Are you sure you want to delete this payment?')) {
      try {
        await deletePayment.mutateAsync({
          id: paymentId,
          invoiceId: invoice.id,
        });
      } catch (error) {
        // Error handling omitted intentionally
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Link to="/invoices">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{invoice.invoice_number}</h1>
            <p className="text-muted-foreground">Invoice Details</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge className={INVOICE_STATUS_COLORS[invoice.status]}>
            {INVOICE_STATUS_LABELS[invoice.status]}
          </Badge>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {invoice.status === 'draft' && (
                <>
                  <DropdownMenuItem asChild>
                    <Link to={`/invoices/${invoice.id}/edit`}>
                      <Edit className="mr-2 h-4 w-4" />
                      Edit Invoice
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowSendDialog(true)}>
                    <Send className="mr-2 h-4 w-4" />
                    Send Invoice
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}

              <DropdownMenuItem onClick={() => downloadInvoicePDF(invoice)}>
                <Download className="mr-2 h-4 w-4" />
                Download PDF
              </DropdownMenuItem>

              <DropdownMenuItem onClick={() => {
                downloadInvoicePDF(invoice);
                window.print();
              }}>
                <Printer className="mr-2 h-4 w-4" />
                Print
              </DropdownMenuItem>

              {invoice.status !== 'cancelled' && invoice.status !== 'paid' && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleCancelInvoice} className="text-red-600">
                    Cancel Invoice
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Invoice Info */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Invoice Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Invoice Number</span>
              <span className="font-medium">{invoice.invoice_number}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Issue Date</span>
              <span>{format(new Date(invoice.issue_date), 'MMM d, yyyy')}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Due Date</span>
              <span>{format(new Date(invoice.due_date), 'MMM d, yyyy')}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Payment Terms</span>
              <span>{invoice.payment_terms || 'Net 30'}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Client Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Organization</span>
              <Link
                to={`/organizations/${invoice.organization?.id}`}
                className="font-medium text-primary hover:underline"
              >
                {invoice.organization?.name}
              </Link>
            </div>
            {invoice.project && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Project</span>
                <Link
                  to={`/projects/${invoice.project.id}`}
                  className="font-medium text-primary hover:underline"
                >
                  {invoice.project.name}
                </Link>
              </div>
            )}
            {invoice.organization?.email && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Email</span>
                <span>{invoice.organization.email}</span>
              </div>
            )}
            {invoice.organization?.phone && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Phone</span>
                <span>{invoice.organization.phone}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Line Items */}
      <Card>
        <CardHeader>
          <CardTitle>Line Items</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Quantity</TableHead>
                <TableHead className="text-right">Unit Price</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoice.line_items?.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.description}</TableCell>
                  <TableCell className="text-right">{item.quantity}</TableCell>
                  <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="mt-6 space-y-2">
            <div className="flex justify-between text-sm">
              <span>Subtotal</span>
              <span>{formatCurrency(invoice.subtotal)}</span>
            </div>
            {invoice.discount_amount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Discount ({((invoice.discount_rate || 0) * 100).toFixed(1)}%)</span>
                <span>-{formatCurrency(invoice.discount_amount)}</span>
              </div>
            )}
            {invoice.tax_amount > 0 && (
              <div className="flex justify-between text-sm">
                <span>Tax ({((invoice.tax_rate || 0) * 100).toFixed(1)}%)</span>
                <span>{formatCurrency(invoice.tax_amount)}</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between font-semibold text-lg">
              <span>Total</span>
              <span>{formatCurrency(invoice.total_amount)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payments */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Payments</CardTitle>
          {invoice.status !== 'cancelled' && remainingBalance > 0 && (
            <Button size="sm" onClick={() => setShowPaymentDialog(true)}>
              <DollarSign className="mr-2 h-4 w-4" />
              Record Payment
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {invoice.payments && invoice.payments.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoice.payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>{format(new Date(payment.payment_date), 'MMM d, yyyy')}</TableCell>
                      <TableCell>{formatCurrency(payment.amount)}</TableCell>
                      <TableCell>{payment.payment_method || '-'}</TableCell>
                      <TableCell>{payment.reference_number || '-'}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeletePayment(payment.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Total Paid</span>
                  <span className="font-medium">{formatCurrency(totalPaid)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Remaining Balance</span>
                  <span className="font-medium">{formatCurrency(remainingBalance)}</span>
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No payments recorded yet
            </p>
          )}
        </CardContent>
      </Card>

      {/* Notes */}
      {invoice.notes && (
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{invoice.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              Record a payment for invoice {invoice.invoice_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="amount">Amount *</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                value={paymentForm.amount}
                onChange={(e) =>
                  setPaymentForm({
                    ...paymentForm,
                    amount: parseFloat(e.target.value) || 0,
                  })
                }
                max={remainingBalance}
              />
              <p className="text-sm text-muted-foreground mt-1">
                Maximum: {formatCurrency(remainingBalance)}
              </p>
            </div>
            <div>
              <Label htmlFor="payment_date">Payment Date *</Label>
              <Input
                id="payment_date"
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) =>
                  setPaymentForm({
                    ...paymentForm,
                    payment_date: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <Label htmlFor="payment_method">Payment Method</Label>
              <Input
                id="payment_method"
                value={paymentForm.payment_method}
                onChange={(e) =>
                  setPaymentForm({
                    ...paymentForm,
                    payment_method: e.target.value,
                  })
                }
                placeholder="Credit Card, Check, Wire Transfer, etc."
              />
            </div>
            <div>
              <Label htmlFor="reference_number">Reference Number</Label>
              <Input
                id="reference_number"
                value={paymentForm.reference_number}
                onChange={(e) =>
                  setPaymentForm({
                    ...paymentForm,
                    reference_number: e.target.value,
                  })
                }
                placeholder="Check #, Transaction ID, etc."
              />
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={paymentForm.notes}
                onChange={(e) =>
                  setPaymentForm({
                    ...paymentForm,
                    notes: e.target.value,
                  })
                }
                placeholder="Additional notes about this payment"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddPayment} disabled={addPayment.isPending}>
              {addPayment.isPending ? 'Recording...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Invoice</DialogTitle>
            <DialogDescription>Send invoice {invoice.invoice_number} via email</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={sendEmail}
                onChange={(e) => setSendEmail(e.target.value)}
                placeholder={invoice.organization?.email || 'client@example.com'}
              />
            </div>
            <div>
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                value={sendMessage}
                onChange={(e) => setSendMessage(e.target.value)}
                placeholder="Please find attached the invoice for your recent order..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendInvoice} disabled={sendInvoiceEmail.isPending || sendInvoice.isPending}>
              <Mail className="mr-2 h-4 w-4" />
              {sendInvoiceEmail.isPending || sendInvoice.isPending ? 'Sending...' : 'Send Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
